<script setup>
import { storeToRefs } from 'pinia'
import Right from 'assets/icons/right.svg'
import { computed } from 'vue'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'
import { useMessageStore } from '~/stores/messages'

// Define props
const props = defineProps({
    shopName: {
        type: String,
        default: '',
        validator(rawValue) {
            return rawValue === undefined || typeof rawValue === 'string'
        }
    }
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Loyalty program
const route = useRoute()
const shopUid = computed(() => route?.params?.shopUid)
const centerRoute = computed(() => route.params?.centerUid)

// Fetch Center and Shop Loyalty Details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)
const { data: loyalty, suspense: loyaltySuspense, isPlaceholderData: isShopLoyaltyPlaceholderData } = useFetchShopLoyalty(shopUid.value)

// Prefetch data on the server side
onServerPrefetch(async () => {
    await centerDetailSuspense() // Wait for center details to load
    await loyaltySuspense() // Wait for shop loyalty data to load
})

// Markdown
const { $md } = useNuxtApp()
const loyaltyDescription = computed(() => $md.render(loyalty.value?.description || ''))

/**
 * Track a loyalty click event.
 *
 * @return {void} No return value.
 */
const trackLoyaltyClick = () => {
    // Track user click
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.SHOP_LOYALTY_REGISTER,
        shopName: props.shopName,
        centerUid: centerDetails.value?.centerUid
    })
}
</script>

<template>
    <PremiumContentBox>
        <template #headline>
            <ContentHeadline :content-headline-config="{ title: staticKeys?.shop_loyalty_headline_web_markdown, renderSplit: true }" />
        </template>

        <template #content>
            <component
                :is="SkeletonPlaceholder"
                v-if="isShopLoyaltyPlaceholderData"
                :skeleton-placeholder-config="{ type: 'multi-headline', tag: 'h3' }"
            />

            <h3 v-else-if="loyalty.name" class="shop-loyalty-headline">
                {{ loyalty.name }}
            </h3>

            <component
                :is="SkeletonPlaceholder"
                v-if="isShopLoyaltyPlaceholderData"
                :skeleton-placeholder-config="{ type: 'text', iterator: 5 }"
            />

            <div v-else-if="loyalty.description" class="shop-loyalty-description" v-html="loyaltyDescription" />

            <h4 v-if="! isShopLoyaltyPlaceholderData" class="shop-loyalty-subline">
                {{ staticKeys?.shops_details_getapp }}
            </h4>

            <NuxtLink
                v-if="loyalty.externalUrls?.web"
                :to="loyalty.externalUrls.web"
                class="shop-loyalty-btn-bar"
                target="_blank"
                rel="noopener noreferrer"
                @click="trackLoyaltyClick()"
            >
                <img
                    v-if="loyalty.logotype?.thumbnail"
                    :src="loyalty.logotype.thumbnail"
                    :alt="loyalty.logotype?.altText"
                    width="200"
                    height="50"
                >
                {{ loyalty?.name }}
                <Right />
            </NuxtLink>
        </template>
    </PremiumContentBox>
</template>

<style lang="scss">
.shop-loyalty-headline {
    font-family: $font-body;
    font-weight: 800;
    font-size: px2rem(20);
    line-height: px2rem(25);
    margin: 0 0 20px;

    @include bp(medium1) {
        margin: 0 0 30px;
    }
}

.shop-loyalty-subline {
    color: $shop-loyalty-content-subline;
    margin: 0 0 40px;

    @include body3-extrabold;
}

.shop-loyalty-description {
    p {
        margin-block-end: 30px;

        @include body1-medium;
    }

    ul {
        margin: 0 0 30px 20px;
        padding: 0;

        @include body1-medium;

        li {
            list-style-type: disc;
            margin: 0 0 7px;
            padding: 0;
        }
    }
}

.shop-loyalty-btn-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: inherit;
    text-decoration: none;

    @include h3-semibold;

    img {
        margin: 0 20px 0 0;
        max-height: 50px;
        width: auto;
        height: auto;
    }

    svg {
        height: 14px;
        margin: 0 0 0 20px;
    }
}

.shop-loyalty-btn-action {
    display: none;
    position: absolute;
    top: 20px;
    right: 30px;
    transform: translate(100%, -100%);

    @include bp(medium1) {
        display: flex;
        align-items: flex-end;
    }

    svg {
        width: 52px;
        height: 34px;
        margin-inline-end: 10px;
        fill: $shop-loyalty-btn-action-fill;

        @include bp(large1) {
            width: 64px;
            height: 42px;
        }
    }

    h3 {
        font-family: $font-headline;
        font-weight: 500;
        font-style: italic;
        font-size: px2rem(30);
        line-height: px2rem(36);
        letter-spacing: -0.5px;
        margin: 0;

        @include bp(medium1) {
            font-size: px2rem(20);
            line-height: px2rem(24);
        }

        @include bp(large2) {
            font-size: px2rem(26);
            line-height: px2rem(32);
        }
    }
}
</style>
